/**
 * ReceiptControl — buyer's «Запросить чек» control for a completed order (ТЗ №2доп §19-20).
 *
 * Renders one of three states for a *completed* order (and nothing otherwise):
 *  - ``none`` + ``can_request_receipt`` → the «Запросить чек» button. On success the order
 *    returns to work, its chat reopens and the seller is notified (all server-side).
 *  - ``requested`` → a status hint (the request is in flight; reply lands in the order chat).
 *  - ``fulfilled`` → the «уже выдан» note with a shortcut to support.
 *
 * The server is authoritative: the request is idempotent by ``receipt_request_state`` and the
 * button disappears once it leaves ``none``.
 */
import { useState } from "react";

import {
  requestOrderReceipt,
  type OrderStatus,
  type ReceiptRequestState,
} from "@entities/order";
import { ApiError } from "@shared/api";

import styles from "./ReceiptControl.module.css";

export interface ReceiptControlProps {
  readonly orderId: number;
  readonly status: OrderStatus;
  readonly canRequest: boolean;
  readonly state: ReceiptRequestState | string;
  /** Re-pull the order after a successful request so the new state shows. */
  readonly onRequested: () => void;
  /** Open the support screen (used by the «fulfilled» note). */
  readonly onOpenSupport: () => void;
  /** ``compact`` trims paddings for the list card; ``panel`` is the order screen default. */
  readonly variant?: "panel" | "compact";
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { readonly detail?: unknown } | null;
    if (
      payload !== null &&
      typeof payload === "object" &&
      typeof payload.detail === "string"
    ) {
      return payload.detail;
    }
  }
  return "Не удалось запросить чек. Попробуйте ещё раз.";
}

export function ReceiptControl({
  orderId,
  status,
  canRequest,
  state,
  onRequested,
  onOpenSupport,
  variant = "panel",
}: ReceiptControlProps): JSX.Element | null {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The purchase-receipt flow only applies to completed orders.
  if (status !== "completed") {
    return null;
  }

  const request = (): void => {
    if (sending) {
      return;
    }
    setSending(true);
    setError(null);
    requestOrderReceipt(orderId)
      .then(() => {
        onRequested();
      })
      .catch((err: unknown) => {
        setError(errorText(err));
      })
      .finally(() => {
        setSending(false);
      });
  };

  const wrapClass = variant === "compact" ? styles.wrapCompact : styles.wrap;

  if (state === "fulfilled") {
    return (
      <div className={wrapClass}>
        <p className={styles.text}>
          Чек уже был запрошен и выдан. Если остались вопросы — поддержка.
        </p>
        <button type="button" className={styles.linkButton} onClick={onOpenSupport}>
          Написать в поддержку
        </button>
      </div>
    );
  }

  if (state === "requested") {
    return (
      <div className={wrapClass}>
        <p className={styles.text} role="status">
          Запрос на чек отправлен. Продавец пришлёт подтверждение покупки в чат заказа.
        </p>
      </div>
    );
  }

  if (!canRequest) {
    return null;
  }

  return (
    <div className={wrapClass}>
      <p className={styles.text}>
        Нужен чек или подтверждение покупки? Запросите его у продавца.
      </p>
      {error !== null ? (
        <p className={styles.errorNote} role="alert">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        className={styles.button}
        disabled={sending}
        onClick={request}
      >
        {sending ? "Запрашиваем…" : "Запросить чек"}
      </button>
    </div>
  );
}
